import { Box, Flex, Grid, Text, Tooltip } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import ChartWrapper from '~/pages/Dashboards/CallReporting/Shared/ChartWrapper'
import { ColorMaskKeys } from '~/themes/colors/colorPalette'
import { textStyleKeys } from '~/themes/textStyles'

interface HeatmapProps<T> {
  data: HeatmapData<T>[]
  yAxisLabels: string[]
  xAxisLabels: string[]
  title?: string
  colorScheme?: ColorMaskKeys
  convertValue?: (value: number, minValue: number, maxValue: number) => number
  formatValue?: (
    day: string,
    time: string,
    value: number,
    data: T,
    index: number,
  ) => string
  optionComponents?: React.ReactNode[]
}

export const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const allDayTimeLabels = [
  '12 am',
  '1 am',
  '2 am',
  '3 am',
  '4 am',
  '5 am',
  '6 am',
  '7 am',
  '8 am',
  '9 am',
  '10 am',
  '11 am',
  '12 pm',
  '1 pm',
  '2 pm',
  '3 pm',
  '4 pm',
  '5 pm',
  '6 pm',
  '7 pm',
  '8 pm',
  '9 pm',
  '10 pm',
  '11 pm',
]

export const daytimeTimeLabels = [
  '5 am',
  '6 am',
  '7 am',
  '8 am',
  '9 am',
  '10 am',
  '11 am',
  '12 pm',
  '1 pm',
  '2 pm',
  '3 pm',
  '4 pm',
  '5 pm',
  '6 pm',
  '7 pm',
  '8 pm',
  '9 pm',
  '10 pm',
]

export interface HeatmapData<T> {
  value: number
  data: T
}

const defaultConvertValue = (
  value: number,
  minValue: number,
  maxValue: number,
) => {
  return (value - minValue) / (maxValue - minValue)
}

const defaultFormatValue = (
  day: string,
  time: string,
  value: number,
  _: any,
  _2: any,
) => {
  return `${value}`
}

const getEvenlySpacedTimeLabels = (
  labels: string[],
  maxLabels: number = 6,
): string[] => {
  const totalLabels = labels.length
  const stepSize = Math.floor(totalLabels / (maxLabels - 1))
  const selectedLabels = labels.filter((_, index) => index % stepSize === 0)
  const finalLabels = [labels[0], ...selectedLabels, labels[totalLabels - 1]]
  return Array.from(new Set(finalLabels))
}

const Heatmap = <T,>({
  data,
  yAxisLabels,
  xAxisLabels,
  title = 'Heatmap',
  colorScheme = 'blue.500',
  convertValue,
  formatValue,
  optionComponents = [],
}: HeatmapProps<T>) => {
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    setIsLoaded(true)
  }, [])

  if (!data || data.length === 0) {
    return (
      <Box
        bg="white"
        borderRadius="md"
        boxShadow="md"
        p="1rem"
        width="100%"
        display="flex"
        flexDir="column"
        alignItems="center"
        justifyContent="center"
        flex="1"
      >
        <Text>No grid data</Text>
      </Box>
    )
  }
  const sortedData = [...data].sort((a, b) => a.value - b.value)
  const minValue = sortedData[0].value
  const maxValue = sortedData[sortedData.length - 1].value

  const getCellColor = (value: number): string => {
    let opacity = (convertValue ?? defaultConvertValue)(
      value,
      minValue,
      maxValue,
    )
    opacity = Math.ceil(opacity * 10) * 10
    return opacity == 0 ? 'transparent' : `mask.${colorScheme}.${opacity}`
  }

  const visibleXAxisLabels = getEvenlySpacedTimeLabels(xAxisLabels)

  return (
    <ChartWrapper>
      <Flex alignItems="center" justifyContent="space-between" mb="1rem">
        <Text textStyle={textStyleKeys.Body2_Bold}>{title}</Text>

        <Flex alignItems="center">
          {optionComponents.map((optionComponent, index) => (
            <Box key={index} ml="0.5rem">
              {optionComponent}
            </Box>
          ))}
        </Flex>
      </Flex>

      <Grid
        templateColumns={`60px repeat(${xAxisLabels.length}, 1fr)`}
        templateRows={`auto`}
        gap="2px"
        width="100%"
      >
        {yAxisLabels.map((day, rowIndex) => (
          <React.Fragment key={rowIndex}>
            <Flex
              align="center"
              justify="center"
              bg="transparent"
              height="38px"
            >
              <Text textStyle={textStyleKeys.Body3} textColor="gray.700">
                {day}
              </Text>
            </Flex>

            {xAxisLabels.map((_, colIndex) => {
              const dataIndex = rowIndex * xAxisLabels.length + colIndex
              const transformIndex = rowIndex + colIndex
              const cellValue = data[dataIndex].value
              return (
                <Tooltip
                  key={`${day}-${colIndex}`}
                  label={(formatValue ?? defaultFormatValue)(
                    day,
                    xAxisLabels[colIndex],
                    cellValue,
                    data[dataIndex].data,
                    dataIndex,
                  )}
                  hasArrow
                  placement="top"
                >
                  <Flex
                    align="center"
                    justify="center"
                    bg={getCellColor(cellValue)}
                    borderRadius="2px"
                    minH="100%"
                    sx={{
                      opacity: isLoaded ? 1 : 0,
                      transition: `opacity 0.4s ease-in-out ${transformIndex * 0.02}s, transform 0.2s ease-in-out`,
                      zIndex: 1,
                      ':hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 0 4px 2px #00000033`,
                        zIndex: 2,
                      },
                    }}
                  />
                </Tooltip>
              )
            })}
          </React.Fragment>
        ))}
      </Grid>

      <Flex>
        <Box width="64px" />
        <Grid
          templateColumns={`repeat(${visibleXAxisLabels.length}, ${
            xAxisLabels.length > 4 ? 'auto' : '1fr'
          })`}
          gap="2px"
          width="100%"
          justifyContent="space-between"
        >
          {visibleXAxisLabels.map((label, colIndex) => (
            <Flex
              key={colIndex}
              align="center"
              justify="center"
              bg="transparent"
              padding="0.375rem"
            >
              <Text textStyle={textStyleKeys.Body3} textColor="gray.700">
                {label}
              </Text>
            </Flex>
          ))}
        </Grid>
      </Flex>
    </ChartWrapper>
  )
}

export default Heatmap
